<template lang="pug">
yz-dialog(v-if="showDialog" v-model="showDialog" persistent max-width="960" scrollable)
  yz-card
    yz-dialog-normal-header(:title="dialogTitle" @close="onCloseDialog")
    yz-dialog-normal-content.pa-6
      template(v-if="isFetchingPaymentMethods")
        v-row(no-gutters)
          v-col(cols="7")
            v-skeleton-loader(type="article@3")
          v-col(cols="5")
            v-skeleton-loader(type="article@1")

      v-form(v-else ref="form")
        v-row
          v-col(cols="7")
            //- payment type radio
            template(v-if="!isRefundMode")
              .yz-medium-body-sm.text-neutral-700.mb-1 Payment type
              yz-radio-group(v-model="paymentMethod" direction="horizontal")
                yz-radio(value="self-pay" label="Self-pay")
                yz-radio(value="panel" label="Panel")

            //- payment method dropdown
            .yz-medium-body-sm.text-neutral-700.mt-4.mb-1 Payment method
            yz-dropdown(v-model="selectedPaymentId" :items="paymentItems" item-text="name" item-value="id" placeholder="Choose payment method" size="lg" required :rules="[v => !!v || 'Payment method is required']" :disabled="Boolean(transaction)")

            //- payment date
            .yz-medium-body-sm.text-neutral-700.mt-4.mb-1 Payment date
            yz-text-field(v-model="formattedDate" placeholder="Select date" prepend-inner-icon="$yzi-calendar" readonly clickable size="lg" @click="showDatePicker = true")

            //- amount
            .yz-medium-body-sm.text-neutral-700.mt-4.mb-1 Amount
            yz-text-field(v-model="amount" placeholder="0" :prefix="amountPrefix" type="number" size="lg" required :rules="rules.amount")

            //- payment notes
            .yz-medium-body-sm.text-neutral-700.mt-4.mb-1 Payment notes
            yz-textarea(v-model="notes" placeholder="Enter your payment notes here..." rows="4" auto-grow)

            //- file preview
            .mt-4
              yz-file-preview(v-model="allSyncedFiles" :to-delete-items.sync="uploadedFileToDelete" :truncate-length="10" @deleted="onDeletedUploadedFiles")

          //- right column
          v-col(cols="5")
            yz-card.pa-3(color="yz-neutral-200" outlined)
              .d-flex.justify-space-between.align-center
                .yz-medium-body-sm.text-neutral-700 Invoice total
                .yz-medium-body-sm.text-neutral-700 {{ invoice.total_amount | displayPrice($currency) }}
              .d-flex.justify-space-between.align-center.mt-1
                .yz-regular-body-xs.text-neutral-500 Paid amount
                .yz-medium-body-xs.text-neutral-700 {{ totalPaid | displayPrice($currency) }}
              yz-divider.my-2
              .d-flex.justify-space-between.align-center
                .yz-medium-body-sm.text-neutral-700 Amount due
                .yz-heading-6.text-red-500 {{ totalBalance | displayPrice($currency) }}

            //- recorded payments
            yz-card.pa-3.mt-3(v-if="transactions.length" color="yz-neutral-200" outlined)
              .yz-medium-body-xs.text-neutral-500 Recorded payments
              template(v-for="(txn, index) in transactions")
                .d-flex.justify-space-between.align-center.mt-2(:key="'txn-' + index")
                  .yz-regular-body-xs.text-neutral-700 {{ txn.payment_method }}
                  .yz-medium-body-xs.text-neutral-700 {{ txn.amount | displayPrice($currency) }}

          //- upload photo (full width below both columns)
          v-col(cols="12")
            .yz-medium-body-sm.text-neutral-700.mb-3 Upload photo
            v-row(align="center")
              v-col.grow
                yz-upload-card(v-model="uploadedPhotos" multiple append-files accept="image/*" primary-title="Click to upload" secondary-title="or drag and drop" description="JPG, PNG (max 5MB)" upload-mode="immediate" @upload-batch-complete="onUploadBatchCompleted")
              v-col.shrink
                yz-upload-qr-databin(:qr-size="75" @databin-created="onQrCreated" @error="onQrError")
              v-col(cols="4")
                .yz-medium-body-xs.text-neutral-700.mb-1 Add from other devices
                .yz-regular-body-xs.text-neutral-700 Scan the QR code to upload photos from another device

            //- databin sync (hidden)
            yz-databin-sync(v-if="databinKey" v-model="allSyncedFiles" :added-items.sync="uploadedPhotos" :databin-key="databinKey" @error="onDatabinError" ref="databinSync")

    yz-dialog-normal-actions(save-label="Save" cancel-label="Cancel" :loading="isLoading" @save="onSave" @cancel="onCloseDialog")

  //- date picker dialog
  yz-dialog(v-model="showDatePicker" max-width="380px")
    yz-card
      yz-dialog-normal-content.px-2.pt-2.pb-0
        yz-date-picker(v-model="paymentDate" scrollable @change="showDatePicker = false")

</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    open: {
      type: Boolean,
      default: false
    },
    transaction: {
      type: Object,
      default: null
    },
    lead: {
      type: Number,
      default: null
    },
    mode: {
      type: String,
      default: 'payment'
    },
    context: {
      type: String,
      default: 'reservation'
    }
  },
  data () {
    return {
      isFetchingPaymentMethods: false,
      isLoading: false,
      paymentMethod: 'self-pay',
      selectedPaymentId: null,
      paymentDate: DateTime.local().toFormat('yyyy-MM-dd'),
      amount: null,
      notes: null,
      selfpays: [],
      panels: [],
      showDatePicker: false,
      // Upload state
      databinKey: null,
      allSyncedFiles: [],
      uploadedPhotos: [],
      uploadedFileToDelete: [],
      rules: {
        amount: [
          v => !!v || 'This field is required',
          v => v >= 0.01 || `Amount should be more than ${this.$currency.symbol} 0`
        ]
      }
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      invoice: 'reservationPreorder/getInvoice',
      transactions: 'reservationPreorder/getTransactions',
      leadPid: 'reservationPreorder/getLeadPid',
      isHq: 'hq/getIsHq',
      hqSelectedCompany: 'hq/getSelectedCompany'
    }),
    isRefundMode () {
      return this.mode === 'refund'
    },
    dialogTitle () {
      return this.isRefundMode ? 'Record new refund' : 'Record new payment'
    },
    amountPrefix () {
      return this.isRefundMode ? '- ' + this.$currency.symbol : this.$currency.symbol
    },
    showDialog: {
      get () {
        return this.open
      },
      set (newValue) {
        this.$emit('update:open', newValue)
      }
    },
    paymentItems () {
      return this.paymentMethod === 'panel' ? this.panels : this.selfpays
    },
    selectedPayment () {
      return this.paymentItems.find(i => i.id === this.selectedPaymentId) || null
    },
    formattedDate () {
      if (!this.paymentDate) return ''
      return DateTime.fromISO(this.paymentDate).toFormat('dd/MM/yyyy')
    },
    totalPaid () {
      if (!this.invoice.pid || !this.transactions.length) return 0
      return this.transactions.reduce((sum, t) => sum + t.amount, 0)
    },
    totalBalance () {
      if (!this.invoice.pid) return 0
      return this.invoice.total_amount - this.totalPaid
    }
  },
  watch: {
    paymentMethod () {
      this.selectedPaymentId = null
    },
    mode: {
      immediate: true,
      handler (val) {
        if (val === 'refund') {
          this.paymentMethod = 'self-pay'
        }
      }
    }
  },
  mounted () {
    this.fetchPaymentMethods()
  },
  methods: {
    ...mapActions(['showMessage']),
    setData () {
      if (!this.transaction) return
      this.amount = Math.abs(Number(this.transaction.amount)) / this.$currency.multiplier
      this.notes = this.transaction.internal_description
      this.paymentMethod = this.transaction.channel
      this.paymentDate = this.transaction.paid_datetime
        ? DateTime.fromISO(this.transaction.paid_datetime).toFormat('yyyy-MM-dd')
        : DateTime.local().toFormat('yyyy-MM-dd')
      this.$nextTick(() => {
        if (this.transaction.channel === 'panel') {
          this.selectedPaymentId = Number(this.transaction.ref_id)
        } else {
          this.selectedPaymentId = Number(this.transaction.ref_id)
        }
      })
    },
    fetchPaymentMethods () {
      this.isFetchingPaymentMethods = true
      const params = {
        company: this.isHq ? this.hqSelectedCompany.id : this.company.id,
        enabled: true,
        fields: 'id,name,channel,verification_link,remarks,display_remarks,panel',
        page_size: -1
      }
      this.$axios.get('/payment_methods', { params })
        .then(resp => {
          if (resp.data && resp.data.results) {
            this.panels = resp.data.results.filter(p => p.channel === 'panel')
            this.selfpays = resp.data.results.filter(p => p.channel === 'self-pay')
          }
        })
        .catch(() => {})
        .finally(() => {
          this.isFetchingPaymentMethods = false
          if (this.transaction) {
            this.setData()
          }
        })
    },
    async onSave () {
      if (!this.$refs.form.validate()) {
        this.showMessage(['error', 'Please fill out all required fields'])
        return
      }
      this.isLoading = true
      try {
        const amountInCent = Math.round(Number(this.amount) * this.$currency.multiplier)
        const finalAmount = this.isRefundMode ? -amountInCent : amountInCent
        const documentIds = this.allSyncedFiles.map(f => f.hid).filter(Boolean)
        let transactionUid

        if (this.transaction) {
          // Edit mode — patch existing transaction
          const payload = {
            amount: finalAmount,
            lead: this.lead,
            channel: this.paymentMethod,
            ref_id: this.selectedPayment?.id,
            payment_method: this.selectedPayment?.name,
            internal_description: this.notes
          }
          if (this.paymentMethod === 'panel') {
            payload.panel = this.panels.find(p => p.id === this.selectedPaymentId)?.panel?.id ?? null
          }
          if (documentIds.length) {
            const existingDocIds = (this.transaction.documents || []).map(d => d.hid).filter(Boolean)
            payload.documents = [...existingDocIds, ...documentIds]
          }
          await this.$axios.patch(`/transactions/${this.transaction.uid}`, payload)
          transactionUid = this.transaction.uid
          const endpoint = `/transactions/${this.transaction.uid}`
          const activityLabel = this.isRefundMode ? 'refund' : 'payment'
          const activity = `Edit ${activityLabel} (${this.selectedPayment?.name})`
          this.logUserActivity(endpoint, payload, null, 'PATCH', this.transaction.uid, activity)
          const amount = this.$options.filters.displayPrice(finalAmount, this.$currency)
          const editTag = this.isRefundMode ? 'edit_refund' : 'edit_transaction'
          this.createActivityLog(editTag, `${this.selectedPayment?.name} amount ${amount} was updated`)
        } else {
          // Create mode — post new transaction
          const payload = {
            amount: finalAmount,
            gateway: 'direct',
            lead: this.lead,
            paid: this.paymentMethod === 'self-pay',
            channel: this.paymentMethod,
            ref_id: this.selectedPayment?.id,
            payment_method: this.selectedPayment?.name,
            internal_description: this.notes
          }
          if (this.paymentMethod === 'panel') {
            payload.panel = this.panels.find(p => p.id === this.selectedPaymentId)?.panel?.id ?? null
          }
          const resp = await this.$axios.post('/transactions/', payload)
          transactionUid = resp.data.uid
          const endpoint = '/transactions/'
          const activityLabel = this.isRefundMode ? 'refund' : 'payment'
          const activity = `Add ${activityLabel} (${this.selectedPayment?.name})`
          this.logUserActivity(endpoint, payload, resp.data, 'POST', null, activity)
          const amount = this.$options.filters.displayPrice(finalAmount, this.$currency)
          const createTag = this.isRefundMode ? 'add_refund' : 'add_transaction'
          this.createActivityLog(createTag, `${this.selectedPayment?.name} amount ${amount} was ${this.isRefundMode ? 'refunded' : 'added'}`)

          // Attach documents if any
          if (documentIds.length) {
            await this.$axios.patch(`/transactions/${transactionUid}`, { documents: documentIds })
          }
        }

        // Clean up deleted uploaded files
        await this.cleanupDeletedFiles()

        this.$emit('updated')
        this.showDialog = false
      } catch (err) {
        let message = 'Unable to complete request'
        if (err.response?.data) {
          if (err.response.data.detail) {
            message = err.response.data.detail
          } else {
            const data = JSON.stringify(err.response.data).replace(/[^\w.\s:]/g, '').replace(/[.:]/g, match => match + ' ')
            if (data) message = data
          }
        }
        this.showMessage(['error', message])
      } finally {
        this.isLoading = false
      }
    },
    async cleanupDeletedFiles () {
      const filesToDelete = this.uploadedFileToDelete.map(f => f.hid).filter(Boolean)
      if (filesToDelete.length) {
        await this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(() => {})
      }
    },
    onCloseDialog () {
      // Clean up synced files on cancel
      const filesToDelete = []
      if (this.allSyncedFiles.length) {
        filesToDelete.push(...this.allSyncedFiles.map(f => f.hid).filter(Boolean))
      }
      if (this.uploadedFileToDelete.length) {
        filesToDelete.push(...this.uploadedFileToDelete.map(f => f.hid).filter(Boolean))
      }
      if (filesToDelete.length) {
        this.$axios.post('/documents/bulk_delete', { documents: filesToDelete }).catch(() => {})
      }
      this.showDialog = false
    },
    onQrCreated (keyData) {
      this.databinKey = keyData.key
    },
    onQrError (error) {
      console.error('QR Code Error:', error)
      this.showMessage(['error', 'Unable to generate QR code'])
    },
    onDatabinError (error) {
      console.error('Databin Sync Error:', error)
      this.showMessage(['error', 'Error syncing files'])
    },
    onUploadBatchCompleted (uploadResults) {
      if (this.$refs.databinSync && uploadResults.length > 0) {
        this.$refs.databinSync.addItems(uploadResults)
      }
    },
    onDeletedUploadedFiles (deletedFiles) {
      if (this.$refs.databinSync && deletedFiles.length > 0) {
        this.$refs.databinSync.removeItems(deletedFiles)
      }
    },
    logUserActivity (endpoint, payload, response, method, id, activity) {
      const data = {
        company: this.company.id,
        object_id: id || null,
        object_ref: this.context,
        activity,
        raw_data: { endpoint, payload, response, method }
      }
      this.$axios.post('/users/logs', data).catch(() => {})
    },
    createActivityLog (tag, activity) {
      const payload = {
        company: this.company.id,
        app_model: 'form.lead',
        object_id: this.leadPid,
        tag,
        group: this.context,
        activity
      }
      this.$axios.post('/actions', payload).catch(() => {})
    }
  }
}
</script>
